import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentData,
  QueryConstraint,
  QueryDocumentSnapshot
} from "firebase/firestore";
import { db } from "../firebase/config";
import { Scale, ScaleFormData } from "../types/scale.types";

const COLLECTION_NAME = 'scales';

// Generate point labels dynamically based on scale type and points
export const generatePointLabels = (
  scaleType: Scale['scaleType'],
  numberOfPoints: number
): { point: number; label: string; value: number }[] => {
  const labels: { point: number; label: string; value: number }[] = [];

  const templates: Record<string, Record<number, string>> = {
    likert: {
      1: "Strongly Disagree",
      2: "Disagree",
      3: "Neutral",
      4: "Agree",
      5: "Strongly Agree",
      6: "Slightly Agree",
      7: "Agree",
    },
    frequency: {
      1: "Never",
      2: "Rarely",
      3: "Sometimes",
      4: "Often",
      5: "Always",
      6: "Almost Always",
      7: "Always",
    },
    importance: {
      1: "Not Important",
      2: "Slightly Important",
      3: "Moderately Important",
      4: "Important",
      5: "Very Important",
      6: "Highly Important",
      7: "Extremely Important",
    },
  };

  for (let i = 1; i <= numberOfPoints; i++) {
    let label = `Point ${i}`;

    if (templates[scaleType] && templates[scaleType][i]) {
      label = templates[scaleType][i];
    } else if (scaleType === "forcedChoice") {
      label = i === 1 ? "Option A" : i === 2 ? "Option B" : `Option ${String.fromCharCode(64 + i)}`;
    } else if (scaleType === "sjt") {
      const sjtLabels = ["Most Effective", "Effective", "Less Effective", "Least Effective", "Ineffective", "Very Ineffective"];
      label = sjtLabels[i - 1] || `Option ${i}`;
    }

    labels.push({
      point: i,
      label,
      value: scaleType === "sjt" ? numberOfPoints - i + 1 : i,
    });
  }
  return labels;
};

// Create a new scale
export const createScale = async (
  scaleData: Omit<ScaleFormData, 'createdAt' | 'updatedAt'>,
  userId: string
): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...scaleData,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating scale:", error);
    throw error;
  }
};

// Get all scales with filtering and pagination
export const getScales = async (filters?: {
  searchTerm?: string;
  scaleType?: string;
  isActive?: boolean;
}) => {
  try {
    const constraints: QueryConstraint[] = [];

    if (filters?.scaleType) {
      constraints.push(where('scaleType', '==', filters.scaleType));
    }

    if (filters?.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }

    constraints.push(orderBy('createdAt', 'desc'));

    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const snapshot = await getDocs(q);

    let scales: Scale[] = [];
    snapshot.forEach((doc) => {
      scales.push({ id: doc.id, ...doc.data() } as Scale);
    });

    // Search filter on client side
    if (filters?.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      scales = scales.filter(scale =>
        scale.scaleName.toLowerCase().includes(term) ||
        scale.description?.toLowerCase().includes(term)
      );
    }

    return { scales, lastDoc: null };
  } catch (error) {
    console.error("Error getting scales:", error);
    throw error;
  }
};

// Get single scale by ID
export const getScaleById = async (scaleId: string): Promise<Scale | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, scaleId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Scale;
    }
    return null;
  } catch (error) {
    console.error("Error getting scale:", error);
    throw error;
  }
};

// Update scale
export const updateScale = async (
  scaleId: string,
  scaleData: Partial<ScaleFormData>
): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, scaleId);
    await updateDoc(docRef, {
      ...scaleData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating scale:", error);
    throw error;
  }
};

// Delete scale
export const deleteScale = async (scaleId: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, scaleId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting scale:", error);
    throw error;
  }
};

// Toggle scale active status
export const toggleScaleStatus = async (scaleId: string, isActive: boolean): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, scaleId);
    await updateDoc(docRef, {
      isActive,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling scale status:", error);
    throw error;
  }
};

// Bulk delete scales
export const bulkDeleteScales = async (scaleIds: string[]): Promise<void> => {
  try {
    const promises = scaleIds.map(id => deleteScale(id));
    await Promise.all(promises);
  } catch (error) {
    console.error("Error bulk deleting scales:", error);
    throw error;
  }
};

// Get scale statistics
export const getScaleStatistics = async (): Promise<{
  total: number;
  byType: Record<string, number>;
  active: number;
  inactive: number;
}> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const scales = snapshot.docs.map(doc => doc.data());

    const byType: Record<string, number> = {};
    let active = 0;

    scales.forEach(scale => {
      const type = scale.scaleType;
      byType[type] = (byType[type] || 0) + 1;

      if (scale.isActive) active++;
    });

    return {
      total: scales.length,
      byType,
      active,
      inactive: scales.length - active,
    };
  } catch (error) {
    console.error("Error getting scale statistics:", error);
    throw error;
  }
};